<template>
  <v-dialog v-model="show" max-width="600px" persistent @keydown.esc="hide">
    <template v-slot:activator="{ on }">
      <v-btn
        small
        text
        :disabled="isDisabled"
        v-on="on"
        :class="
          isOpenBtnColorPrimary ? 'primary accent-4 white--text mr-2' : 'mr-2'
        "
      >
        {{
          `${feedback.stage} ${feedback.form.nameLocale.value.split(" ")[0]}`
        }}
        {{ isSubmitted ? "✓" : "" }}
      </v-btn>
    </template>

    <v-card
      :id="
        `mentorshipId_${mentorship.id}_feedback.assignedId_${feedback.assignedId}`
      "
    >
      <v-card-title>
        <span class="title text--secondary my-2  text-uppercase">
          {{ $t("profile.mentorship.feedback.title") }}
        </span>
        <v-spacer />
        <v-btn icon class="mx-2" @click="printHtml">
          <v-icon>print</v-icon>
        </v-btn>
        <v-btn icon @click="hide"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle :class="cardTextClass">
        <div>
          <span class="font-weight-medium">
            {{ $t("management.mentorship.feedback.column.code") }}:
          </span>
          {{ feedback.code }}
        </div>
        <div>
          <span class="font-weight-medium">
            {{ $t("management.mentorship.feedback.column.revision") }}:
          </span>
          {{ feedback.revision }}
        </div>
        <div>
          <span class="font-weight-medium">
            {{ $t("management.mentorship.feedback.column.name") }}:
          </span>
          {{ feedback.form.nameLocale.value }}
        </div>
        <div>
          <span class="font-weight-medium">
            {{ $t("profile.mentorship.column.tag") }}:
          </span>
          {{ mentorship.tag.description || mentorship.tag.name }}
        </div>
        <div>
          <span class="font-weight-medium">
            {{ $t("profile.mentorship.column.mentor") }}:
          </span>
          {{ mentorship.mentor | nameId }}
        </div>
        <div>
          <span class="font-weight-medium">
            {{ $t("profile.mentorship.column.mentee") }}:
          </span>
          {{ mentorship.employee | nameId }}
        </div>
        <div>
          <span class="font-weight-medium">
            {{ $t("management.mentorship.feedback.edit.submitOrder.title") }}:
          </span>
          {{ getSubmitOrderText(feedback) }}
        </div>
      </v-card-subtitle>
      <v-card-text>
        <!-- mentor -->
        <v-card
          class="mb-4"
          :class="isMentorFromDisabled ? 'custom-disabled' : ''"
        >
          <v-card-title :class="cardTextClass">
            <span class="title">{{
              $t("profile.mentorship.feedback.mentorForm")
            }}</span>
            <v-spacer />
            <v-chip
              v-if="feedback.mentorSubmittedForm"
              small
              color="green lighten-4 success--text"
            >
              {{ $t("profile.mentorship.feedback.mentorSubmitted") }}:{{
                feedback.mentorSubmittedForm.dateTime | date
              }}</v-chip
            >
          </v-card-title>
          <v-card-text :class="cardTextClass">
            <feedback-form
              formName="mentorForm"
              :formContents="
                feedback.mentorSubmittedForm
                  ? feedback.mentorSubmittedForm.contents
                  : feedback.form.mentorContents
              "
              @submit="submit"
              :disabled="isMentorFromDisabled"
            />
          </v-card-text>
        </v-card>
        <!-- mentee -->
        <v-card :class="isMenteeFormDisabled ? 'custom-disabled' : ''">
          <v-card-title>
            <span class="title">{{
              $t("profile.mentorship.feedback.menteeForm")
            }}</span>
            <v-spacer />
            <v-chip
              v-if="feedback.menteeSubmittedForm"
              small
              color="green lighten-4 success--text"
            >
              {{ $t("profile.mentorship.feedback.menteeSubmitted") }}:{{
                feedback.menteeSubmittedForm.dateTime | date
              }}</v-chip
            >
          </v-card-title>
          <v-card-text :class="cardTextClass">
            <feedback-form
              formName="menteeForm"
              :formContents="
                feedback.menteeSubmittedForm
                  ? feedback.menteeSubmittedForm.contents
                  : feedback.form.menteeContents
              "
              @submit="submit"
              :disabled="isMenteeFormDisabled"
            />
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text color="grey darken-1" class="ma-2" @click="hide">
          {{ $t("dialog.cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from "vuex";

import FeedbackCheckbox from "@/components/mentorship/control/feedback/Checkbox.vue";
import FeedbackForm from "@/components/mentorship/control/feedback/Form.vue";

import SubmitMentorshipFeedbackQuery from "@/graphql/mutation/mentorshipFeedback/SubmitMentorshipFeedback.gql";
import MentorshipFeedbackAssignedHTMLQuery from "@/graphql/queries/mentorship/feedback/MentorshipFeedbackAssignedHTML.gql";
import MyOldAccountsQuery from "@/graphql/queries/MyOldAccounts.gql";
import { remapContent } from "@/utils/mentorshipFeedback.js";

import { printHtml } from "@/utils/print";

import { matchesUserOrOldID } from "@/utils/mentorshipActions";

export default {
  props: {
    feedback: {
      type: Object,
      required: true,
    },
    isPreviousStageSubmitted: {
      type: Boolean,
      required: true,
    },
    mentorship: {
      type: Object,
      required: true,
    },
  },
  components: {
    FeedbackCheckbox,
    FeedbackForm,
  },
  data() {
    return {
      show: false,
      showOtherForm: false,
      cardTextClass: "black--text",
      myOldAccounts: [],
    };
  },
  computed: {
    ...mapGetters(["role"]),
    ...mapState(["me"]),
    isSubmitted() {
      return (
        !!this.feedback.mentorSubmittedForm &&
        !!this.feedback.menteeSubmittedForm
      );
    },
    isDisabled() {
      if (
        !this.isManager &&
        !this.isMentor &&
        !this.isMentee &&
        !this.role("safetyRepresentative", "departmentManager", "admin")
      ) {
        return true;
      }

      if (this.isPreviousStageSubmitted) {
        return false;
      }

      return true;
    },
    isOpenBtnColorPrimary() {
      if (!this.isPreviousStageSubmitted) {
        return false;
      }

      if (this.isSubmitted) {
        return false;
      }

      if (
        !this.feedback.mentorSubmitFirst &&
        !this.feedback.menteeSubmitFirst
      ) {
        return (
          (this.isMentor && !this.feedback.mentorSubmittedForm) ||
          (this.isMentee && !this.feedback.menteeSubmittedForm)
        );
      }

      if (
        this.isMentee &&
        this.feedback.menteeSubmitFirst &&
        !this.feedback.menteeSubmittedForm
      ) {
        return true;
      }

      if (
        this.isMentee &&
        this.feedback.mentorSubmitFirst &&
        !!this.feedback.mentorSubmittedForm &&
        !this.feedback.menteeSubmittedForm
      ) {
        return true;
      }

      if (
        this.isMentor &&
        this.feedback.mentorSubmitFirst &&
        !this.feedback.mentorSubmittedForm
      ) {
        return true;
      }

      if (
        this.isMentor &&
        this.feedback.menteeSubmitFirst &&
        !!this.feedback.menteeSubmittedForm &&
        !this.feedback.mentorSubmittedForm
      ) {
        return true;
      }

      return false;
    },
    isManager() {
      return this.mentorship.employee.managerId == this.me.id;
    },

    isMentor() {
      return this.mentorship.mentor && this.mentorship.mentor.id == this.me.id;
    },
    isMentorFromDisabled() {
      if (this.mentorship.status == "confirm") {
        return true;
      }
      return (
        !this.isPreviousStageSubmitted ||
        !this.isMentor ||
        !!this.feedback.mentorSubmittedForm ||
        (this.feedback.menteeSubmitFirst && !this.feedback.menteeSubmittedForm)
      );
    },
    isMentee() {
      if (!this.mentorship.employee) {
        return false;
      }

      const employeeId = Number(this.mentorship.employee.id);

      return matchesUserOrOldID(this.me, employeeId, this.myOldAccounts);
    },
    isMenteeFormDisabled() {
      if (this.mentorship.status == "confirm") {
        return true;
      }
      return (
        !this.isPreviousStageSubmitted ||
        !this.isMentee ||
        !!this.feedback.menteeSubmittedForm ||
        (this.feedback.mentorSubmitFirst && !this.feedback.mentorSubmittedForm)
      );
    },
  },
  methods: {
    submit(contents) {
      const contentsRemapped = this.remapContents(contents);
      this.$apollo
        .mutate({
          mutation: SubmitMentorshipFeedbackQuery,
          variables: {
            mentorshipId: this.mentorship.id,
            assignedId: this.feedback.assignedId,
            contents: JSON.stringify(contentsRemapped),
          },
        })
        .then((res) => {
          this.$emit("saved", res.data.mentorshipFeedbacks);
          this.hide();
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        });
    },
    hide() {
      this.show = false;
    },
    remapContents(items) {
      return items.map((item) => remapContent(item));
    },
    getSubmitOrderText(feedback) {
      if (!feedback.menteeSubmitFirst && !feedback.mentorSubmitFirst) {
        return this.$t("management.mentorship.feedback.edit.submitOrder.none");
      } else if (feedback.menteeSubmitFirst && !feedback.mentorSubmitFirst) {
        return this.$t(
          "management.mentorship.feedback.edit.submitOrder.menteeFirst"
        );
      } else if (!feedback.menteeSubmitFirst && feedback.mentorSubmitFirst) {
        return this.$t(
          "management.mentorship.feedback.edit.submitOrder.mentorFirst"
        );
      }
    },
    printHtml() {
      this.$apollo
        .query({
          query: MentorshipFeedbackAssignedHTMLQuery,
          variables: {
            id: this.feedback.assignedId,
            language: this.$i18n.locale,
          },
        })
        .then((res) => {
          printHtml(res.data.html);
        });
    },
  },
  apollo: {
    myOldAccounts: {
      query: MyOldAccountsQuery,
    },
  },
};
</script>

<style scoped>
@media print {
  .hidden-print {
    display: none !important;
  }
}

custom-disabled {
  pointer-events: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
